/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';

import { Box, Card, Chip, Collapse, Divider, Grid, Icon, IconButton, TablePagination, Tooltip } from '@mui/material';
import { ChipStatus, IChipsSettingProps } from '../chip-status/ChipStatus';
import { TextFieldReadOnly } from '../text-field-readonly/TextFieldReadOnly';
import { formatDate, formatDateTime } from '../../utils';
import { useTranslation } from 'react-i18next';

interface ICardListProps {
    columns: ICardColumns[];
    rows: any[];
    hasChildren: boolean | true;
    childrenTitle?: string;
    childrenComponent?: any
    cardSize?: number | 12;
    actions?: ICardActions[] | undefined;
    setRowExpanded: (data: any) => void;
    fillHeight?: boolean;
    hidePagination?: boolean;
}

export interface ICardColumns {
    field: string;
    label: string;
    size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    type: 'text' | 'boolean' | 'currency' | 'date' | 'dateTime' | 'decimal' | 'component' | 'percent' | 'number';
    visible: boolean | true;
    isChip: boolean | false;
    chipSettings?: IChipsSettingProps[];
    hasFieldLine: boolean | false;
    hasLabelChip?: boolean | false;
    hasToolTip?: boolean | true;
    colorValue?: string;
    noWrap?: boolean;
}
export interface ICardActions {
    id: string;
    toolTip: string;
    variantButton?: string;
    icon: string;
    iconColor: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | undefined;
    setAction: (data: any) => void;
}
export const CardList: React.FC<ICardListProps> = ({
    columns,
    rows,
    hasChildren,
    childrenTitle,
    childrenComponent,
    cardSize,
    actions,
    setRowExpanded,
    fillHeight = false,
    hidePagination = false }) => {

    const [list, setList] = useState<any[]>([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);
    const { t } = useTranslation();
    const visibleRows = hidePagination ? rows : list;

    useEffect(() => {
        if (hidePagination) {
            setList(rows);
            return;
        }

        const from = page === 0 ? page : page === 1 ? (page + rowsPerPage) - 1 : (page * rowsPerPage);
        setList(rows.slice(from, rowsPerPage + from));
    }, [hidePagination, rowsPerPage, rows, page]);

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
        const from = newPage === 0 ? newPage : newPage === 1 ? (newPage + rowsPerPage) - 1 : (newPage * rowsPerPage);
        setList(rows.slice(from, rowsPerPage + from));
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box sx={{ display: 'flex', width: '100%', height: fillHeight ? '100%' : undefined, minHeight: fillHeight ? 0 : undefined }}>
            {visibleRows.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', height: fillHeight ? '100%' : undefined, minHeight: fillHeight ? 0 : undefined }}>
                    <Box sx={{ flex: fillHeight ? 1 : undefined, minHeight: fillHeight ? 0 : undefined, overflowY: fillHeight ? 'auto' : undefined, overflowX: 'hidden', pr: fillHeight ? 0.5 : undefined }}>
                        <Grid container spacing={1}>
                            {visibleRows.map((row, index) => (
                                <Grid item xs={12} md={cardSize} key={index}>
                                    <CardRow
                                        key={index}
                                        columns={columns}
                                        row={row}
                                        hasChildren={hasChildren}
                                        childrenTitle={childrenTitle}
                                        setRowExpanded={setRowExpanded}
                                        childrenComponent={childrenComponent}
                                        cardSize={cardSize}
                                        actions={actions}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    {!hidePagination && rows.length > 10 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                boxShadow: 'none',
                                overflow: 'hidden',
                                flexShrink: 0,
                                minHeight: 52,
                                '& .MuiTablePagination-root': {
                                    minHeight: 52,
                                },
                                '& .MuiTablePagination-toolbar': {
                                    minHeight: 52,
                                },
                            }}
                        >
                            <TablePagination
                                component='div'
                                count={rows.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Box>
                    ) : (undefined)}
                </Box>
            ) : (
                <Card sx={{ display: 'flex', borderRadius: 1, width: '100%', p: 2, pb: 2, gap: 1, cursor: 'auto', transition: 'all .3s', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <Grid display={'flex'} container spacing={0} >
                        <Grid display={'flex'} item xs={12} md={12} justifyContent={'center'}>
                            <Chip icon={<Icon>search_off</Icon>} label={t('components.cardList.message')} color={'primary'} variant="outlined" size='medium' />
                        </Grid>
                    </Grid>
                </Card>
            )
            }
        </Box>
    );
};

function CardRow(props: { columns: ICardColumns[], row: any[], hasChildren: boolean | true, childrenTitle?: string, setRowExpanded: (data: any) => void, childrenComponent?: any, cardSize?: number | 12, actions?: ICardActions[] | undefined }) {

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleExpandRow = useCallback((row: any, isOpen: boolean) => {
        setOpen(oldValue => !oldValue);
        setLoading(true);
        
        row ?? props.setRowExpanded(row);
        !isOpen ? props.setRowExpanded(row) : undefined;
    }, []);

    const handleAction = useCallback((action: any, row: any) => {
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement) {
            activeElement.blur();
        }

        action(row);
        setOpen(false);
    }, []);

    const handleCardClick = useCallback((action: any, row: any, isOpen: boolean) => {
        if (action) {
            handleAction(action, row);
        }
        else {
            props.hasChildren ? handleExpandRow(row, isOpen) : undefined;
        }
    }, []);

    return (
        <React.Fragment>
            <Card
                onClick={props.hasChildren ? () => handleCardClick(null, props.row, open) : undefined}
                sx={{
                    display: 'flex',
                    borderRadius: 1,
                    width: '99.8%',
                    p: 2,
                    mb: 0,
                    gap: 1,
                    cursor: props.hasChildren ? 'pointer' : 'auto',
                    transition: 'all .3s',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                    ':hover': {
                        borderColor: 'secondary.main',
                        boxShadow: '0 6px 16px rgba(16, 24, 40, 0.06)'
                    }
                }}>
                <Grid container spacing={1} >
                    {props.columns.map((column: ICardColumns, index: any) => {
                        const label = column.label;
                        let value = '';

                        for (let i = 0; i < Object.values<any>(props.row).length; i++) {
                            if (Object.keys(props.row)[i].valueOf() === column.field) {
                                value = Object.values<any>(props.row)[i];
                                break;
                            }
                        }
                        /* eslint-disable indent */
                        switch (column.type) {
                            case 'currency':
                                value = 'R$ ' + value;
                                break;
                            case 'boolean':
                                value = String(value);
                                break;

                            case 'date':
                                value = String(formatDate(value));
                                break;

                            case 'dateTime':
                                value = String(formatDateTime(value));
                                break;
                            
                            case 'decimal':
                                value = Number(value).toFixed(2);
                                break;

                            case 'number':
                                value = Number(value).toFixed(0);
                                break;

                            case 'percent':
                                value = value ? (parseFloat(value)).toFixed(2) + '%' : '0.00%'; // Converte para porcentagem com duas casas decimais
                                break;
                                
                            default:
                                break;
                        }
                        if (column.visible) {
                            return (
                                <React.Fragment key={index}>
                                    <Grid item xs={12} md={column.size}>
                                        <TextFieldReadOnly
                                            id={index}
                                            label={label}
                                            value={!column.isChip ? value : <ChipStatus label={label} value={value} chipSettings={column.chipSettings}/>}
                                            hasDivider={column.hasFieldLine}
                                            hasToolTip={column.hasToolTip}
                                            colorValue={column.colorValue}
                                            noWrap={column.noWrap}
                                        />
                                    </Grid>
                                </React.Fragment>
                            );
                        }
                    })}
                    <Grid item xs={12} md={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Box display='flex' justifyContent='flex-start'>
                            {props.hasChildren && (
                                <Tooltip title={open ? 'Collapse row' : 'Expand row'}>
                                    <IconButton
                                        aria-label="expand row"
                                        size="small"
                                    >
                                        {open ? <Icon fontSize='small'>expand_less</Icon> : <Icon fontSize='small'>expand_more</Icon>}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {props.actions ? props.actions.map((action, index) => {
                                return (
                                    <Tooltip title={action.toolTip} key={index}>
                                        <IconButton
                                            id={action.id}
                                            key={index}
                                            size='small'
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleCardClick(action.setAction, props.row, false);
                                            }}
                                        >
                                            <Icon fontSize='small' color={action.iconColor}>{action.icon}</Icon>
                                        </IconButton>
                                    </Tooltip>
                                );
                            }) : undefined}
                        </Box>
                    </Grid>
                </Grid>
            </Card>
            <Collapse in={open} timeout='auto' unmountOnExit>
                <Grid container gap={1} ml={6} pr={6} mt={1}>
                    <Icon color='disabled'>subdirectory_arrow_right</Icon>{props.childrenTitle}
                    <Box display='flex' width='100%' gap={1} >
                        {props.childrenComponent}
                    </Box>
                </Grid>
            </Collapse>
        </React.Fragment>
    );
}
